import React, { useMemo, useRef, useState } from 'react';
import styles from './UserPicker.module.css';
import clx from '../../common/clx';
import Popover from '../../common/Popover';
import Ico from '../../common/Ico';
import { actorDisplayName } from '../actorName';

const AVATAR_COLORS = ['#1570EF', '#7839EE', '#067647', '#B54708', '#C11574', '#0E7090'];
const ICO_CHEVDOWN = 'm6 9 6 6 6-6';
const ICO_CHECK    = 'M20 6 9 17l-5-5';
const ICO_USER     = 'M20 21a8 8 0 1 0-16 0M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z';
const ICO_SEARCH   = 'M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z';

function colorFor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || '').length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function displayNameFor(user) {
  if (!user) return '';
  return user.full_name || actorDisplayName(user.email);
}

function initialsFor(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Avatar({ user, size = 'sm' }) {
  return (
    <span
      className={clx(styles.avatar, size === 'lg' && styles.avatarLg)}
      style={{ background: colorFor(user?.email) }}
      aria-hidden="true"
    >
      {initialsFor(displayNameFor(user))}
    </span>
  );
}

/**
 * Searchable user picker. Used both as a per-row assignee editor (`meResolvesToEmail`)
 * and as the assignee filter (where "Me" stays a distinct, symbolic filter value).
 */
export default function UserPicker({
  users = [],
  currentUser,
  value,
  onChange,
  extraOptions = [],
  meResolvesToEmail = false,
  placeholder = 'Unassigned',
  disabled = false,
  loading = false,
  align = 'left',
  className,
}) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedUser = useMemo(
    () => users.find((u) => u.email === value) || (currentUser?.email === value ? currentUser : null),
    [users, value, currentUser]
  );
  const selectedExtra = extraOptions.find((o) => o.value === value);
  const isMe = value === 'me';
  const meSelected = isMe || (meResolvesToEmail && !!currentUser && value === currentUser.email);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? users.filter((u) => displayNameFor(u).toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      : users;
    return [...list].sort((a, b) => displayNameFor(a).localeCompare(displayNameFor(b)));
  }, [users, query]);

  const close = () => { setOpen(false); setQuery(''); };

  const choose = (nextValue) => {
    if (nextValue === 'me' && meResolvesToEmail) {
      onChange(currentUser?.email || '');
    } else {
      onChange(nextValue);
    }
    close();
  };

  let triggerContent;
  if (isMe) {
    triggerContent = (<><Avatar user={currentUser} /><span className={styles.triggerLabel}>Me</span></>);
  } else if (selectedUser) {
    triggerContent = (
      <>
        <Avatar user={selectedUser} />
        <span className={styles.triggerLabel} title={selectedUser.email}>{displayNameFor(selectedUser)}</span>
      </>
    );
  } else {
    const label = selectedExtra ? selectedExtra.label : placeholder;
    triggerContent = (
      <>
        <span className={styles.unassignedIcon}><Ico d={ICO_USER} size={11} strokeWidth={1.8} /></span>
        <span className={styles.triggerLabel}>{label}</span>
      </>
    );
  }

  const neutralValue = extraOptions[0]?.value;
  const isActive = meSelected || !!selectedUser || (selectedExtra && selectedExtra.value !== neutralValue);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={clx(styles.trigger, isActive && styles.triggerActive, className)}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
      >
        {triggerContent}
        <Ico d={ICO_CHEVDOWN} size={12} className={styles.triggerChev} />
      </button>

      <Popover anchorRef={anchorRef} open={open} onClose={close} align={align} minWidth={240}>
        <div className={styles.searchRow}>
          <Ico d={ICO_SEARCH} size={13} className={styles.searchIcon} />
          <input
            autoFocus
            className={styles.searchInput}
            placeholder="Search people…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className={styles.pinnedGroup}>
          {extraOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={clx(styles.row, value === opt.value && styles.rowSelected)}
              onClick={() => choose(opt.value)}
            >
              <span className={styles.unassignedIcon}><Ico d={ICO_USER} size={11} strokeWidth={1.8} /></span>
              <span className={styles.rowText}><span className={styles.rowName}>{opt.label}</span></span>
              {value === opt.value && <Ico d={ICO_CHECK} size={13} className={styles.rowCheck} />}
            </button>
          ))}
          {currentUser && (
            <button
              type="button"
              className={clx(styles.row, meSelected && styles.rowSelected)}
              onClick={() => choose('me')}
            >
              <Avatar user={currentUser} />
              <span className={styles.rowText}>
                <span className={styles.rowName}>Me</span>
                <span className={styles.rowEmail}>{currentUser.email}</span>
              </span>
              {meSelected && <Ico d={ICO_CHECK} size={13} className={styles.rowCheck} />}
            </button>
          )}
        </div>

        <div className={styles.list}>
          {loading && <div className={styles.empty}>Loading people…</div>}
          {!loading && filteredUsers.length === 0 && <div className={styles.empty}>No matching people</div>}
          {!loading && filteredUsers.map((user) => (
            <button
              key={user.email}
              type="button"
              className={clx(styles.row, value === user.email && styles.rowSelected)}
              onClick={() => choose(user.email)}
            >
              <Avatar user={user} />
              <span className={styles.rowText}>
                <span className={styles.rowName}>{displayNameFor(user)}</span>
                <span className={styles.rowEmail}>{user.email}</span>
              </span>
              {value === user.email && <Ico d={ICO_CHECK} size={13} className={styles.rowCheck} />}
            </button>
          ))}
        </div>
      </Popover>
    </>
  );
}
